const DASHBOARD_SNAPSHOT_PREFIX = "student.dashboard.snapshot.";
const DASHBOARD_SNAPSHOT_TTL = 1000 * 60 * 15;
const FRESH_SNAPSHOT_WINDOW = 1000 * 4;

type DashboardFetcher<T> = () => Promise<T>;

let inFlightDashboardRequest: Promise<any> | null = null;

export type DashboardSnapshot<T = any> = {
  cachedAt: number;
  studentId: number;
  data: T;
};

export function getDashboardSnapshotKey(studentId: number) {
  return `${DASHBOARD_SNAPSHOT_PREFIX}${studentId}`;
}

export function getDashboardRequestParams() {
  const now = new Date();
  const streakTo = now.toISOString().split("T")[0];
  const streakFrom = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 30,
  )
    .toISOString()
    .split("T")[0];

  return {
    streak_from: streakFrom,
    streak_to: streakTo,
    assignment_start_date: streakFrom,
    assignment_end_date: streakTo,
    attendance_start_date: "2020-01-01",
    attendance_end_date: "2030-12-31",
    quiz_start_date: streakFrom,
    quiz_end_date: streakTo,
    exam_start_date: streakFrom,
    exam_end_date: streakTo,
  };
}

export function readDashboardSnapshot<T = any>(studentId: number) {
  try {
    const raw = localStorage.getItem(getDashboardSnapshotKey(studentId));
    if (!raw) return null;

    const snapshot = JSON.parse(raw) as DashboardSnapshot<T>;
    if (
      snapshot.studentId !== studentId ||
      Date.now() - snapshot.cachedAt > DASHBOARD_SNAPSHOT_TTL
    ) {
      localStorage.removeItem(getDashboardSnapshotKey(studentId));
      return null;
    }

    return snapshot;
  } catch {
    localStorage.removeItem(getDashboardSnapshotKey(studentId));
    return null;
  }
}

export function writeDashboardSnapshot(studentId: number, data: any) {
  localStorage.setItem(
    getDashboardSnapshotKey(studentId),
    JSON.stringify({ studentId, cachedAt: Date.now(), data }),
  );
}

export function clearDashboardSnapshots() {
  inFlightDashboardRequest = null;
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(DASHBOARD_SNAPSHOT_PREFIX)) {
      localStorage.removeItem(key);
    }
  }
}

export function isFreshDashboardSnapshot(snapshot: DashboardSnapshot | null) {
  if (!snapshot) return false;
  return Date.now() - snapshot.cachedAt <= FRESH_SNAPSHOT_WINDOW;
}

export function fetchSharedDashboard<T>(fetcher: DashboardFetcher<T>) {
  if (inFlightDashboardRequest) {
    return inFlightDashboardRequest as Promise<T>;
  }

  inFlightDashboardRequest = fetcher().finally(() => {
    inFlightDashboardRequest = null;
  });

  return inFlightDashboardRequest as Promise<T>;
}
